// Review → Files and agreement → Documents (design §15): documents staff add, each internal or shown to the firm, and
// the firm's own Documents uploads (the anything_else slot), which staff can move into a migration-file slot.
// Showing a document to the firm always says so first; nothing here sends email.
import { useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { SLOT_IDS, slotDef, type IntakeFileView, type Review, type SlotId, type StaffDocumentView } from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { fmtMonthDay } from '../lib/format';
import { Banner, Button, Dialog, MenuButton, Pill, cx } from '../ui';

const ACCEPT = '.csv,.xlsx,.xls,.pdf,.docx,.doc,.txt,.zip,.json,.tsv';
type Row = { kind: 'ours'; d: StaffDocumentView } | { kind: 'firm'; f: IntakeFileView };

const message = (e: unknown, fallback: string) => (e instanceof ApiError && e.code !== 'internal' && e.code !== 'network' ? e.message : fallback);

async function download(path: string, setErr: (s: string | null) => void) {
  setErr(null);
  const win = window.open('', '_blank');
  try {
    const res = await api.get<{ url: string }>(path);
    if (win) { win.opener = null; win.location.href = res.url; } else window.location.href = res.url;
  } catch (e) {
    win?.close();
    setErr(e instanceof ApiError && e.code === 'scan_pending' ? 'This file is still being scanned.'
      : e instanceof ApiError && e.code === 'scan_rejected' ? 'This file failed the malware scan and can’t be downloaded.' : 'Could not download the file.');
  }
}

export function Documents({ r, reload }: { r: Review; reload: () => Promise<void> }) {
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState<{ d: StaffDocumentView; action: 'show' | 'remove' } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const editable = r.status !== 'withdrawn';
  const firm = r.header.firmName;
  const firmUploads = r.files.slots.find((s) => s.id === 'anything_else')?.files ?? [];
  const rows: Row[] = [
    ...r.documents.map((d) => ({ kind: 'ours' as const, d })),
    ...firmUploads.map((f) => ({ kind: 'firm' as const, f })),
  ].sort((a, b) => (b.kind === 'ours' ? b.d.addedAt : b.f.uploadedAt).localeCompare(a.kind === 'ours' ? a.d.addedAt : a.f.uploadedAt));

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    setErr(null);
    try { await fn(); await reload(); } catch (e) { setErr(message(e, fallback)); }
  };
  const setShared = (d: StaffDocumentView, shared: boolean) => run(() => api.patch(`/api/admin/documents/${d.id}`, { shared }), 'That change did not save.');
  const move = (f: IntakeFileView, slotId: SlotId) => run(() => api.patch(`/api/admin/files/${f.id}`, { slotId }), 'Could not move the file.');

  return (
    <div className="rounded-card border border-line bg-white p-[18px]">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-[15px] font-semibold">Documents</h3>
        {editable && <Button variant="outline" size="sm" onClick={() => setAdding(true)}>+ Add document</Button>}
      </div>
      {err && <Banner tone="error" role="alert" className="mb-2">{err}</Banner>}
      {rows.length === 0
        ? <p className="m-0 text-sm text-muted">None yet. Documents you add, and files the firm shares from its status page, show here.</p>
        : (
          <ul className="m-0 flex list-none flex-col p-0 text-sm" aria-label="Documents">
            {rows.map((row) => {
              const name = row.kind === 'ours' ? row.d.fileName : row.f.fileName;
              const scan = row.kind === 'ours' ? row.d.scan : row.f.scan;
              const at = row.kind === 'ours' ? row.d.addedAt : row.f.uploadedAt;
              return (
                <li key={row.kind === 'ours' ? row.d.id : row.f.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line-soft py-2 last:border-b-0">
                  <div className="min-w-0 flex-[1_1_220px]">
                    {scan === 'clean'
                      ? <button type="button" className="border-0 bg-transparent p-0 text-left font-semibold text-brand underline [overflow-wrap:anywhere]"
                        onClick={() => void download(row.kind === 'ours' ? `/api/admin/documents/${row.d.id}/download` : `/api/admin/files/${row.f.id}/download`, setErr)}>{name}</button>
                      : <span className="text-muted [overflow-wrap:anywhere]">{name}</span>}
                    <div className={cx('text-[12.5px]', scan === 'rejected' ? 'font-semibold text-bad' : 'text-muted')}>
                      {scan === 'pending' ? 'Scanning' : scan === 'rejected' ? 'Rejected by scan'
                        : row.kind === 'ours' ? `${row.d.addedBy} · ${fmtMonthDay(at)}${row.d.sharedAt && row.d.firmOpenedAt ? ` · Firm opened ${fmtMonthDay(row.d.firmOpenedAt)}` : ''}`
                        : `${r.header.contact?.name || r.header.inviteName} (firm) · ${fmtMonthDay(at)}`}
                    </div>
                  </div>
                  {row.kind === 'ours'
                    ? <Pill tone={row.d.sharedAt ? 'warn' : 'neutral'} className="!px-2 !py-0 !text-xs">{row.d.sharedAt ? 'Firm sees this' : 'Only us'}</Pill>
                    : <Pill tone="info" className="!px-2 !py-0 !text-xs">Firm’s upload</Pill>}
                  {editable && row.kind === 'ours' && (
                    <MenuButton label={`Actions for ${name}`} entries={() => [
                      row.d.sharedAt
                        ? { label: 'Hide from the firm', onSelect: () => void setShared(row.d, false) }
                        : { label: 'Show to the firm…', onSelect: () => setConfirm({ d: row.d, action: 'show' }) },
                      { label: 'Remove…', danger: true, onSelect: () => setConfirm({ d: row.d, action: 'remove' }) },
                    ]} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-ink-2 hover:bg-panel">
                      <MoreHorizontal aria-hidden size={16} />
                    </MenuButton>
                  )}
                  {editable && row.kind === 'firm' && (
                    <select aria-label={`Move ${name} to a migration file`} className="h-8 max-w-[190px] rounded-lg border border-line-strong bg-white px-1 text-[13px]"
                      value="" onChange={(e) => { if (e.target.value) void move(row.f, e.target.value as SlotId); }}>
                      <option value="">Move to…</option>
                      {SLOT_IDS.filter((s) => s !== 'anything_else').map((s) => <option key={s} value={s}>{slotDef(s).title}</option>)}
                    </select>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      <AddDocument open={adding} r={r} firm={firm} onClose={() => setAdding(false)} onAdded={async () => { setAdding(false); await reload(); }} />
      <Dialog open={!!confirm} title={confirm?.action === 'show' ? 'Show this document to the firm?' : 'Remove this document?'} onClose={() => setConfirm(null)}
        footer={<>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant={confirm?.action === 'remove' ? 'danger' : 'primary'} onClick={() => {
            const c = confirm!;
            setConfirm(null);
            void (c.action === 'show' ? setShared(c.d, true) : run(() => api.del(`/api/admin/documents/${c.d.id}`), 'Could not remove the document.'));
          }}>{confirm?.action === 'show' ? 'Show to the firm' : 'Remove'}</Button>
        </>}>
        <p className="m-0 text-sm">
          {confirm?.action === 'show'
            ? `“${confirm.d.fileName}” will show on ${firm}’s status page under Documents, and the firm can download it. No email is sent.`
            : `“${confirm?.d.fileName}” will be removed${confirm?.d.sharedAt ? ` from ${firm}’s status page and` : ''} from this review.`}
        </p>
      </Dialog>
    </div>
  );
}

function AddDocument({ open, r, firm, onClose, onAdded }: { open: boolean; r: Review; firm: string; onClose: () => void; onAdded: () => Promise<void> }) {
  const [file, setFile] = useState<File | null>(null);
  const [share, setShare] = useState<'staff' | 'firm' | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const close = () => { setFile(null); setShare(null); setErr(null); onClose(); };
  const add = async () => {
    if (!file || !share) return;
    setBusy(true);
    setErr(null);
    const form = new FormData();
    form.append('share', share);
    form.append('file', file, file.name);
    try { await api.post(`/api/admin/onboardings/${r.id}/documents`, form); setFile(null); setShare(null); await onAdded(); }
    catch (e) { setErr(message(e, 'The document did not upload. Try again.')); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} title="Add a document" onClose={close}
      footer={<><Button onClick={close}>Cancel</Button><Button variant="primary" disabled={!file || !share || busy} onClick={() => void add()}>{busy ? 'Uploading…' : 'Add document'}</Button></>}>
      <div className="flex flex-col gap-3.5 text-sm">
        <div className="flex flex-wrap items-center gap-2.5">
          <input ref={input} type="file" accept={ACCEPT} className="sr-only" tabIndex={-1} aria-hidden="true" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <Button variant="secondary" size="sm" onClick={() => input.current?.click()}>{file ? 'Choose another file' : 'Choose file'}</Button>
          <span className={cx('[overflow-wrap:anywhere]', file ? 'font-semibold' : 'text-muted')}>{file ? file.name : 'CSV, Excel, PDF, Word, text, ZIP or JSON, up to 100 MB'}</span>
        </div>
        <fieldset className="m-0 border-0 p-0">
          <legend className="mb-1.5 p-0 font-semibold">Who can see it?</legend>
          {([['staff', 'Only our team', 'Internal. The firm never sees it.'], ['firm', 'The firm too', 'The firm can download it from its status page.']] as const).map(([v, label, hint]) => (
            <label key={v} className={cx('mb-2 flex cursor-pointer items-start gap-2.5 rounded-[10px] border px-3 py-2.5', share === v ? 'border-brand bg-brand-tint' : 'border-line')}>
              <input type="radio" name="share" className="mt-0.5 h-4 w-4 accent-brand" checked={share === v} onChange={() => setShare(v)} />
              <span>{label}<span className="block text-[12.5px] text-muted">{hint}</span></span>
            </label>
          ))}
        </fieldset>
        {share === 'firm' && <p className="m-0 font-semibold text-warn">Shows on {firm}’s status page under Documents. No email is sent.</p>}
        {err && <Banner tone="error" role="alert">{err}</Banner>}
      </div>
    </Dialog>
  );
}
